import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr, fmtMoney, downloadCsv } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { RotateCcw, Download, AlertCircle } from "lucide-react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";

const REASON_COLORS = {
  "damaged":       "#EF4444", // red
  "expired":       "#F59E0B", // amber
  "not_satisfied": "#8B5CF6", // violet
  "wrong_item":    "#3B82F6", // blue
  "other":         "#64748B", // slate
  "unspecified":   "#94A3B8", // slate light
};

export default function ReturnsAnalytics() {
  const [from, setFrom] = useState(() => new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/returns-analytics", { params: { from_date: from, to_date: to } });
      setData(d);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [from, to]); // eslint-disable-line

  const pieData = useMemo(() => (data?.by_reason || []).map(r => ({
    name: r.label, value: r.total, count: r.count, reason: r.reason,
  })), [data]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ["أسباب المرتجعات (المدة من " + from + " إلى " + to + ")"],
      ["السبب", "عدد المرتجعات", "الإجمالي"],
      ...(data.by_reason || []).map(r => [r.label, r.count, r.total]),
      [],
      ["أعلى الأصناف المرتجعة"],
      ["الصنف", "إجمالي الكمية", "عدد المرات", "إجمالي المبلغ"],
      ...(data.top_products || []).map(p => [p.product_name, p.quantity, p.count, p.total]),
    ];
    downloadCsv(`returns_analytics_${from}_${to}.csv`, rows);
    toast.success("تم تصدير التقرير إلى Excel/CSV");
  };

  return (
    <div className="space-y-6" data-testid="returns-analytics-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2">
            <RotateCcw className="w-6 h-6 text-amber-600" /> تحليلات المرتجعات
          </h1>
          <p className="text-sm text-muted-foreground mt-1">أسباب المرتجعات + الأصناف الأكثر إعادةً من العملاء</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-xs">من</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" data-testid="from-date-input" />
          </div>
          <div>
            <Label className="text-xs">إلى</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" data-testid="to-date-input" />
          </div>
          <Button variant="outline" onClick={exportCsv} disabled={!data} className="gap-1 h-9" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> تصدير
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-16 text-sm">جاري التحميل...</div>
      ) : !data || data.count === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
          <div className="text-muted-foreground">لا توجد مرتجعات في هذه الفترة</div>
          <div className="text-xs text-muted-foreground/70 mt-1">جرّب توسيع نطاق التاريخ أعلاه</div>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">إجمالي المرتجعات</div>
              <div className="text-2xl font-bold text-primary mt-1" data-testid="kpi-count">{data.count.toLocaleString("ar-EG")}</div>
              <div className="text-[10px] text-muted-foreground mt-1">فاتورة</div>
            </Card>
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">القيمة الإجمالية</div>
              <div className="text-2xl font-bold text-amber-700 mt-1 tabular-nums" data-testid="kpi-total">{data.total.toLocaleString("ar-EG")}</div>
            </Card>
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">عدد الأسباب المسجّلة</div>
              <div className="text-2xl font-bold text-violet-700 mt-1">{(data.by_reason || []).filter(r => r.reason !== "unspecified").length}</div>
            </Card>
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">أصناف مختلفة</div>
              <div className="text-2xl font-bold text-blue-700 mt-1">{(data.top_products || []).length}</div>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-4">
              <h2 className="font-heading font-bold mb-3">توزيع الأسباب</h2>
              <div className="h-80" data-testid="reasons-pie-container">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={(e) => `${e.name}: ${e.count}`}
                    >
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={REASON_COLORS[entry.reason] || "#94A3B8"} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => v.toLocaleString("ar-EG")} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 border-t pt-2 space-y-1">
                {(data.by_reason || []).map(r => (
                  <div key={r.reason} className="flex items-center justify-between text-xs" data-testid={`reason-row-${r.reason}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-sm" style={{ background: REASON_COLORS[r.reason] || "#94A3B8" }} />
                      <span>{r.label}</span>
                      <span className="text-muted-foreground">({r.count})</span>
                    </div>
                    <span className="tabular-nums font-bold">{r.total.toLocaleString("ar-EG")}</span>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-4 overflow-hidden flex flex-col">
              <h2 className="font-heading font-bold mb-3">أعلى الأصناف المرتجعة</h2>
              <div className="flex-1 overflow-auto border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-start">الصنف</TableHead>
                      <TableHead className="text-end">الكمية</TableHead>
                      <TableHead className="text-end">مرّات</TableHead>
                      <TableHead className="text-end">الإجمالي</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.top_products || []).length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">—</TableCell></TableRow>
                    )}
                    {(data.top_products || []).map((p, i) => (
                      <TableRow key={p.product_id || i} data-testid={`top-product-${i}`}>
                        <TableCell className="font-semibold">
                          <span className="me-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-mono">#{i + 1}</span>
                          {p.product_name}
                        </TableCell>
                        <TableCell className="text-end tabular-nums font-bold">{p.quantity.toLocaleString("ar-EG")}</TableCell>
                        <TableCell className="text-end tabular-nums">{p.count}</TableCell>
                        <TableCell className="text-end tabular-nums text-amber-700 font-bold">{p.total.toLocaleString("ar-EG")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
